import TextareaAutosize from 'react-textarea-autosize';
import styled from '@emotion/styled';
import AddCommentIcon from '../../Icons/AddCommentIcon';

export const BarBackground = styled.div`
  background: ${({ theme }) => theme.trackingBar.background};
  width: 100%;
  position: relative;
  min-height: ${({ mode }) => (mode === 'pwa' ? '' : '60px')};
`;

const fontSizesBySize = {
  small: '16px',
  medium: '18px',
  large: '22px',
};

const expandIconWidthBySize = {
  large: '30px',
  medium: '24px',
  small: '20px',
};

const trackingBarPaddingBySize = {
  small: '5px',
  medium: '10px',
  large: '12px',
};
const gapBySizes = {
  small: '5px',
  medium: '7px',
  large: '10px',
};

const SIZES_MAP = {
  small: 20,
  medium: 30,
  large: 35,
};
const getSizeInPixels = (size) => `${SIZES_MAP[size]}px`;

const playPauseIconSizesBySize = {
  small: getSizeInPixels('small'),
  medium: getSizeInPixels('medium'),
  large: getSizeInPixels('large'),
};

export const TrackingBar = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  justify-content: space-between;
  gap: ${({ size }) => gapBySizes[size]};

  align-self: stretch;
  position: relative;
  align-items: center;
  // color: ${({ theme }) => theme.color};
  color: ${({ theme }) => theme.trackingBar.color};
  padding: ${({ size }) => trackingBarPaddingBySize[size]};
  max-height: ${({ mode, size }) =>
    mode === 'pwa' ? '' : getSizeInPixels(size)};
  min-width: 0;
  max-width: 1150px;
  line-height: 30px;
`;

export const Timer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  justify-content: ${({ mode }) => (mode === 'pwa' ? 'space-evenly' : '')};
`;

export const Time = styled.span`
  font-size: ${({ size }) => fontSizesBySize[size]};
  letter-spacing: 1px;
  font-weight: 700;
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
`;

export const CardName = styled.span`
  flex: ${({ mode }) => (mode === 'pwa' ? '1' : '')};
  filter: ${({ isLoading }) => (isLoading ? 'blur(5px)' : '')};
  font-size: ${({ size }) => fontSizesBySize[size]};
  cursor: pointer;
  font-weight: 300;
  letter-spacing: 0.5px;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
  text-overflow: ellipsis;
  > span {
    border-bottom: 2px solid ${({ theme }) => theme.trackingBar.color};
  }
`;

export const Dot = styled.div`
  width: 5px;
  height: 5px;
  // background: ${(props) => props.theme.color};
  background: ${({ theme }) => theme.trackingBar.color};
  margin: 0 10px;
  border-radius: 50%;
  flex: 0 0 auto;
  min-width: 0;
`;

const playBorderWidthBySize = {
  small: `${SIZES_MAP.small / 2}px 0 ${SIZES_MAP.small / 2}px ${getSizeInPixels(
    'small'
  )}`,
  medium: `${SIZES_MAP.medium / 2}px 0 ${
    SIZES_MAP.medium / 2
  }px ${getSizeInPixels('medium')}`,
  large: `${SIZES_MAP.large / 2}px 0 ${SIZES_MAP.large / 2}px ${getSizeInPixels(
    'large'
  )}`,
};

const pauseBorderWidthBySize = {
  small: `0 0 0 ${getSizeInPixels('small')}`,
  medium: `0 0 0 ${getSizeInPixels('medium')}`,
  large: `0 0 0 ${getSizeInPixels('large')}`,
};

export const PlayPauseButton = styled.div`
  padding: 0;
  background: transparent;
  box-sizing: border-box;
  height: ${({ size }) => playPauseIconSizesBySize[size]};
  width: ${({ size }) => playPauseIconSizesBySize[size]};

  border-color: ${({ theme }) =>
    `transparent transparent transparent ${theme.trackingBar.color};`}
  cursor: pointer;

  // play state
  border-style: ${({ isPlay }) => (isPlay ? 'solid' : 'double')};
  border-width: ${({ isPlay, size }) =>
    isPlay ? playBorderWidthBySize[size] : pauseBorderWidthBySize[size]};
  border-radius: ${({ isPlay }) => (isPlay ? '3px' : '0px')};
`;

export const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  right: 20px;
  bottom: 6px;
  cursor: pointer;
  gap: 10px;
`;

export const CommentIcon = styled.div`
  display: flex;
  cursor: pointer;
`;

export const ExpandCollapse = styled.div`
  cursor: pointer;
  text-align: center;
  font-size: calc(${({ size }) => fontSizesBySize[size]} + 2px);
  min-width: ${({ size }) => expandIconWidthBySize[size]};
`;

export const EstimateText = styled.div`
  white-space: nowrap;
  color: white;
  font-size: 14px;
  margin-top: 4px;
  margin-right: 16px;
  flex-basis: 0;
  border: none;
  cursor: pointer;
`;

export const AddworklogCommentIcon = () => (
  <div
    style={{
      marginTop: '8px',
      cursor: 'pointer',
    }}
  >
    <AddCommentIcon
      size="large"
      primaryColor="white"
    />
  </div>
);

export const WorklogCommentForm = styled.div`
  padding: 20px;
  width: 300px;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #afafaf;
  }
`;

export const WorklogComment = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 160px;
`;

export const InputLabel = styled.div`
  // color: #6b778c;
  color: ${({ theme }) => theme.trackingBar.color};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3333333333333333;
  padding: 5px 0;
`;

export const RequiredIndicator = styled.span`
  padding-left: 2px;
  color: rgb(255, 86, 48);
`;

export const Textarea = styled(TextareaAutosize, {
  shouldForwardProp: (prop) => !['invalidMessage', 'isInvalid'].includes(prop),
})`
  width: 100%;

  display: inline;
  margin: 4px 0 12px;

  border: 0;

  -webkit-appearance: none;
  border: none;
  border-radius: 3px;
  box-shadow: ${({ isInvalid }) =>
    isInvalid ? 'inset 0 0 0 2px #eb5a46' : 'inset 0 0 0 2px #dfe1e6'};
  box-sizing: border-box;
  line-height: 20px;
  outline: none;
  padding: 8px 12px;
  transition-duration: 85ms;
  transition-property: background-color, border-color, box-shadow;
  transition-timing-function: ease;

  // background-color: #fafbfc;
  background-color: ${({ theme }) => theme.input.background};
  box-shadow: ${({ isInvalid, theme }) =>
    isInvalid ? theme.input.boxShadowError : theme.input.boxShadow};
  &:hover {
    outline: none;
    box-shadow: ${({ isInvalid, theme }) =>
      isInvalid ? theme.input.boxShadowError : theme.input.boxShadowHovered};
  }
  &:focus {
    outline: none;
    box-shadow: ${({ isInvalid, theme }) =>
      isInvalid ? theme.input.boxShadowError : theme.input.boxShadowFocused};
  }
`;
